import { redirect } from '@tanstack/react-router';

import type { User } from '@/utils/session/fetch-user';
import type { PublicEnv } from '@/utils/session/get-public-env';

import { BASE_PATH } from '@/constants/config';
import { pickUtmValuesFromRawSearch } from '@/schemas/utm';
import { getSafeReturnTo } from '@/utils/get-safe-return-to';

export function isResearcherUser(user: User): boolean {
  return user.roles.includes('researcher');
}

/** Throws a TanStack redirect when the user is a researcher. */
export function redirectResearchersToDashboard(
  user: User,
  publicEnv: PublicEnv,
): void {
  if (!isResearcherUser(user)) {
    return;
  }

  if (publicEnv.WEBSITE_URL.length > 0) {
    throw redirect({ href: `${publicEnv.WEBSITE_URL}/dashboard` });
  }

  throw redirect({ to: '/researchers/projects' });
}

/** The `beforeLoad` location, narrowed to what the login bounce reads. */
export type ParticipantLoginLocation = {
  pathname: string;
  searchStr: string;
  /** Raw `parseSearch` output: values are typed by shape, not validated. */
  search: Record<string, unknown>;
};

/**
 * Redirects an unauthenticated visitor from `/participants/_authenticated/*`
 * to login, carrying the current location forward as `returnTo` so the user
 * lands back where they started once signed in.
 *
 * Also surfaces any `utm_*` params directly on the login URL itself (not
 * just nested inside `returnTo`): the login page's own capture
 * (`useCaptureParticipantUtmFromSearch` in `login.tsx`) reads its own search
 * params, so without this a marketing CTA's attribution would only be
 * captured after the user finishes logging in, and never at all if they
 * bounce (PX-274).
 *
 * `location.search` is raw `parseSearch` output — the guarded route need not
 * validate `utm_*` at all — so the values are coerced on the way out. A
 * numeric campaign id like `?utm_advert_id=123` would otherwise be dropped
 * here and the bounce would record no attribution.
 */
export function redirectToParticipantLogin(
  location: ParticipantLoginLocation,
): never {
  const candidate = `${BASE_PATH}${location.pathname}${location.searchStr}`;
  const safeReturnTo = getSafeReturnTo(candidate);
  throw redirect({
    to: '/participants/login',
    search: {
      ...pickUtmValuesFromRawSearch(location.search),
      ...(safeReturnTo ? { returnTo: safeReturnTo } : {}),
    },
  });
}

/**
 * Ensures the visitor is a logged-in participant (not a researcher).
 * Throws redirect when unauthenticated or researcher.
 *
 * `location` is the `beforeLoad` location and is required, not optional: the
 * login bounce routes the visitor's campaign attribution through
 * {@link redirectToParticipantLogin}, and a guarded route with no way to omit
 * it cannot lose that attribution by forgetting to pass it.
 */
export function requireParticipantSession(
  user: User | null,
  publicEnv: PublicEnv,
  location: ParticipantLoginLocation,
): asserts user is User {
  if (!user) {
    redirectToParticipantLogin(location);
  }

  redirectResearchersToDashboard(user, publicEnv);
}
